"use client";

import { useEffect, useState, useMemo } from "react";
import {
  useRouter,
  useParams,
  useSearchParams,
  usePathname,
} from "next/navigation";
import { AccountHeader } from "@/components/address/AccountHeader";
import { AccountTabs } from "@/components/address/AccountTabs";
import {
  BalanceOverview,
  BalanceOverviewSkeleton,
} from "@/components/address/BalanceOverview";
import { useNetwork } from "@/lib/contexts/NetworkContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Terminal } from "lucide-react";
import AccountNotFound from "../not-found";
import {
  decodeSparkAddress,
  encodeSparkAddress,
  getNetworkFromAddress,
  isValidSparkAddress,
  looksLikePublicKey,
  type NetworkType,
} from "@/lib/spark/address";
import { useAddressSummary, useAddressTransactions } from "@/lib/hooks/useAPI";

const ITEMS_PER_PAGE = 25;

export default function AccountPage() {
  const router = useRouter();
  const params = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { network, setNetwork } = useNetwork();
  const addressParam = params.spark_address as string;

  const [isValidated, setIsValidated] = useState(false);
  const [isValidAddressFormat, setIsValidAddressFormat] = useState(false);
  const [derivedSparkAddress, setDerivedSparkAddress] = useState<string | null>(
    null
  );
  const [decodedPubkey, setDecodedPubkey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const currentNetworkType = useMemo(
    () => network.toUpperCase() as NetworkType,
    [network]
  );

  useEffect(() => {
    if (!addressParam) return;

    setIsValidated(false);
    setErrorMessage(null);
    setIsValidAddressFormat(false);
    setDerivedSparkAddress(null);
    setDecodedPubkey(null);
    setCurrentPage(1);

    const addressNetworkType = getNetworkFromAddress(addressParam);

    if (isValidSparkAddress(addressParam)) {
      setIsValidAddressFormat(true);
      setDerivedSparkAddress(addressParam);

      // Check if the address network matches the current UI network
      if (addressNetworkType && addressNetworkType.toLowerCase() === network) {
        try {
          const pubkey = decodeSparkAddress(addressParam, currentNetworkType);
          setDecodedPubkey(pubkey);
        } catch (e) {
          console.error("Failed to decode valid spark address:", e);
          setErrorMessage("Error processing address.");
          setIsValidAddressFormat(false);
        }
      } else if (addressNetworkType) {
        // Address is for a different network than what we're viewing
        const targetNetwork = addressNetworkType.toLowerCase() as
          | "mainnet"
          | "regtest";

        // Try to decode using the address's actual network type
        try {
          const pubkey = decodeSparkAddress(addressParam, addressNetworkType);
          setDecodedPubkey(pubkey);

          // Update URL to match the address network to prevent infinite loop
          const current = new URLSearchParams(
            Array.from(searchParams.entries())
          );
          current.set("network", targetNetwork);
          const search = current.toString();
          const query = search ? `?${search}` : "";
          router.replace(`${pathname}${query}`);
        } catch (e) {
          console.error("Failed to decode address after network detection:", e);
          setErrorMessage(
            `Error processing address for ${addressNetworkType} network.`
          );
        }
      } else {
        setErrorMessage(
          `Warning: This address is for an unknown network, but you are currently viewing the ${network.toUpperCase()} network. Data might be incorrect.`
        );
      }
    } else if (looksLikePublicKey(addressParam)) {
      // Handle public key case - create proper address for current network
      try {
        const correctAddress = encodeSparkAddress({
          identityPublicKey: addressParam,
          network: currentNetworkType,
        });
        router.replace(`/address/${correctAddress}`);
        return;
      } catch (e) {
        console.error("Failed to encode public key:", e);
        setErrorMessage("Invalid Public Key provided.");
        setIsValidAddressFormat(false);
      }
    } else {
      setErrorMessage("Invalid Account Identifier provided.");
      setIsValidAddressFormat(false);
    }
    setIsValidated(true);
  }, [
    addressParam,
    network,
    router,
    currentNetworkType,
    setNetwork,
    pathname,
    searchParams,
  ]);

  // Ensure currentNetworkType is 'MAINNET' or 'REGTEST' for API calls
  const safeNetworkType = (
    ["MAINNET", "REGTEST"].includes(currentNetworkType)
      ? currentNetworkType
      : "MAINNET"
  ) as "MAINNET" | "REGTEST";

  const {
    data: accountDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
  } = useAddressSummary(addressParam, {
    network: safeNetworkType as "MAINNET" | "REGTEST",
  });

  const {
    data: paginatedTransactions,
    isLoading: isLoadingTxs,
    error: txsError,
  } = useAddressTransactions(addressParam, {
    network: safeNetworkType as "MAINNET" | "REGTEST",
    limit: ITEMS_PER_PAGE,
    offset: (currentPage - 1) * ITEMS_PER_PAGE,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!isValidated && !derivedSparkAddress) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        Validating address...
      </div>
    );
  }

  if (!isValidAddressFormat && isValidated) {
    return <AccountNotFound />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {errorMessage && (
        <Alert variant="destructive" className="mb-4">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Warning</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {derivedSparkAddress && (
        <AccountHeader
          spark_address={derivedSparkAddress}
          is_burn={
            decodeSparkAddress(derivedSparkAddress, currentNetworkType) ===
            "020202020202020202020202020202020202020202020202020202020202020202"
          }
          network={network}
        />
      )}

      {/* Balance Overview */}
      {isLoadingDetails && isValidAddressFormat && <BalanceOverviewSkeleton />}
      {detailsError && isValidAddressFormat && (
        <Alert variant="destructive" className="mb-6">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error Loading Balance</AlertTitle>
          <AlertDescription>{detailsError.message}</AlertDescription>
        </Alert>
      )}
      {accountDetails && isValidAddressFormat && (
        <BalanceOverview balanceData={accountDetails} />
      )}

      {/* Tabs - Pass real transfers, keep tokens mock */}
      {derivedSparkAddress && decodedPubkey && (
        <AccountTabs
          accountDetails={accountDetails}
          transfers={paginatedTransactions}
          isLoadingTransfers={isLoadingTxs}
          errorTransfers={txsError}
          currentUserPubkey={decodedPubkey}
          sparkAddress={derivedSparkAddress}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* Show error for transfers if applicable */}
      {txsError && isValidAddressFormat && (
        <Alert variant="destructive" className="mt-4">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error Loading Transaction History</AlertTitle>
          <AlertDescription>{txsError.message}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
